import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, of } from 'rxjs';

import { ClickProtection } from '../click-protection/click-protection';

type LogLevel = 'log' | 'warn' | 'error';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: `[safeClick], [safe-click]`,
  standalone: true,
})
export class SafeClickDirective implements OnInit, OnDestroy {
  /** Minimum time between valid clicks in ms (default 250ms) */
  @Input() safeTime = 250;

  /** Enable/disable directive logging */
  @Input() enableLogging = true;

  readonly #elRef: ElementRef<HTMLElement>;
  #clickProtection?: ClickProtection<MouseEvent>;
  #unsubscribe?: () => void;
  #preDispatchHook?: (event: MouseEvent) => Observable<boolean>;
  #skipHookOnce = false;

  constructor(elRef: ElementRef<HTMLElement>) {
    this.#elRef = elRef;
    this._log('ctor');
  }

  @HostListener('click', ['$event'])
  click(event: MouseEvent): void {
    this._log('HostListener:click');
    this._interceptClick(event);
  }

  ngOnInit() {
    this._log('init');

    this.#clickProtection = new ClickProtection<MouseEvent>({
      safeTime: this.safeTime,
      enableLogging: this.enableLogging,
      context: SafeClickDirective.name,
    });

    this.#unsubscribe = this.#clickProtection.onValidClick((event) => {
      this._runPreDispatchHook(event);
    });

    this.#elRef.nativeElement.addEventListener('click', this._interceptClick, true);
  }

  ngOnDestroy() {
    this._log('destroy');

    if (this.#unsubscribe) {
      this.#unsubscribe();
    }

    if (this.#clickProtection) {
      this.#clickProtection.destroy();
    }

    this.#elRef.nativeElement.removeEventListener('click', this._interceptClick, true);
  }

  /**
   * Registers a hook executed before re-dispatching the click.
   * If the hook returns false, the click is not re-dispatched.
   * Used by ConfirmClickDirective to insert the confirmation step.
   */
  setPreDispatchHook(hook: ((event: MouseEvent) => Observable<boolean>) | null): void {
    this.#preDispatchHook = hook ?? undefined;
  }

  private _interceptClick = (event: MouseEvent) => {
    this._log('interceptClick');

    // Re-dispatched click after confirmation: let it pass without throttle or hook.
    // stopImmediatePropagation blocks the @HostListener, which is registered after Angular's
    // (click) binding — so the action handler already ran before reaching this point.
    if (this.#skipHookOnce) {
      this.#skipHookOnce = false;
      event.stopImmediatePropagation();
      return;
    }

    event.stopImmediatePropagation();
    event.preventDefault();

    if (this.#clickProtection) {
      this.#clickProtection.click(event);
    }
  };

  private _runPreDispatchHook(event: MouseEvent): void {
    const hook$ = this.#preDispatchHook ? this.#preDispatchHook(event) : of(true);
    hook$.subscribe((confirmed) => {
      if (confirmed) {
        this._triggerOriginalClick(event);
      }
    });
  }

  private _triggerOriginalClick(event: MouseEvent): void {
    this.#skipHookOnce = true;
    const newEvent = new MouseEvent('click', {
      bubbles: event.bubbles,
      cancelable: event.cancelable,
      view: event.view,
      detail: event.detail,
      screenX: event.screenX,
      screenY: event.screenY,
      clientX: event.clientX,
      clientY: event.clientY,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      button: event.button,
      buttons: event.buttons,
      relatedTarget: event.relatedTarget,
    });
    this.#elRef.nativeElement.dispatchEvent(newEvent);
  }

  private _log(message: string, level: LogLevel = 'log'): void {
    if (this.enableLogging) {
      const fullMessage = `${SafeClickDirective.name}: ${message}`;
      console[level](fullMessage);
    }
  }
}
